import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { useRoute } from '@react-navigation/native';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useAppSelector } from '@/store';
import { formatDisplayName } from '@/utils/residents/user';
import { SettingsProfileCard } from '@/components/residents/redesign/settings';
import { PageHeader } from '@/components/residents/redesign';

import AccountInformationView from '@screens/residents/account/desktop/sections/AccountInformationView';
import PaymentMethodsView from '@screens/residents/account/desktop/sections/PaymentMethodsView';
import AutopayView from '@screens/residents/account/desktop/sections/AutopayView';
import ResetPasswordView from '@screens/residents/account/desktop/sections/ResetPasswordView';
import NotificationSettingsView from '@screens/residents/account/desktop/sections/NotificationSettingsView';
import AppearanceView from '@screens/residents/account/desktop/sections/AppearanceView';

type AccountSectionKey =
  | 'accountInformation'
  | 'paymentMethods'
  | 'autopay'
  | 'resetPassword'
  | 'notificationSettings'
  | 'appearance';

type NavItem = {
  key: AccountSectionKey;
  title: string;
  description: string;
};

export const DesktopAccountScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const { user } = useAppSelector((state) => state.auth);
  const [active, setActive] = useState<AccountSectionKey>('accountInformation');

  const shouldShowPayments = user?.customerType !== 'non-leasing occupant';

  const route = useRoute<any>();
  const sectionParam = route?.params?.section as AccountSectionKey | undefined;
  useEffect(() => {
    if (sectionParam && ['accountInformation', 'paymentMethods', 'autopay', 'resetPassword', 'notificationSettings', 'appearance'].includes(sectionParam)) {
      setActive(sectionParam);
    }
  }, [sectionParam]);

  const displayName = useMemo(() => formatDisplayName(user as any), [user]);
  const email = user?.email || '';
  const propertyName = (user as any)?.property?.marketingName || (user as any)?.property?.legalName || '';
  const unitNumber = user?.unitNumberSpace || '';
  const propertyUnit = useMemo(() => {
    const parts: string[] = [];
    if (propertyName) parts.push(propertyName);
    if (unitNumber) parts.push(`Unit ${unitNumber}`);
    return parts.join(' · ');
  }, [propertyName, unitNumber]);

  const initials = useMemo(() => {
    const parts = displayName.split(' ').filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return '';
  }, [displayName]);

  const items: NavItem[] = useMemo(() => {
    const allItems = [
      { key: 'accountInformation' as AccountSectionKey, title: 'Account Information', description: 'Name and email' },
      shouldShowPayments && { key: 'paymentMethods' as AccountSectionKey, title: 'Payment Methods', description: 'View, add, or remove' },
      shouldShowPayments && { key: 'autopay' as AccountSectionKey, title: 'Autopay', description: 'View, add, or remove' },
      { key: 'resetPassword' as AccountSectionKey, title: 'Reset Password', description: 'Secure your account' },
      { key: 'notificationSettings' as AccountSectionKey, title: 'Notification Settings', description: 'Customize your notifications' },
      { key: 'appearance' as AccountSectionKey, title: 'Appearance', description: 'Light and dark theme' },
    ].filter(Boolean) as NavItem[];
    return allItems;
  }, [shouldShowPayments]);

  const renderContent = useCallback(() => {
    switch (active) {
      case 'accountInformation':
        return <AccountInformationView />;
      case 'paymentMethods':
        return <PaymentMethodsView />;
      case 'autopay':
        return <AutopayView />;
      case 'resetPassword':
        return <ResetPasswordView />;
      case 'notificationSettings':
        return <NotificationSettingsView />;
      case 'appearance':
        return <AppearanceView />;
      default:
        return null;
    }
  }, [active]);

  return (
    <View style={styles.root}>
      <View style={styles.left}>
        <ScrollView contentContainerStyle={styles.leftInner} showsVerticalScrollIndicator={false}>
          <PageHeader title="Settings" />

          <View style={styles.profileCardWrapper}>
            <SettingsProfileCard
              displayName={displayName}
              email={email}
              propertyUnit={propertyUnit}
              initials={initials}
            />
          </View>

          {items.map((item) => {
            const isActive = active === item.key;
            return (
              <Hoverable
                key={item.key}
                hover={isActive ? 'none' : 'field'}
                onPress={() => setActive(item.key)}
                style={[styles.navItem, { backgroundColor: colors.card.bg, borderColor: colors.card.border }, isActive && { backgroundColor: colors.accent.blueBg, borderColor: colors.newsfeed.announcementBorder }]}
                accessibilityRole="button"
              >
                <View style={styles.navCopy}>
                  <Text style={[styles.navTitle, { color: colors.text.secondary }, isActive && { color: colors.text.primary }]}>
                    {item.title}
                  </Text>
                  <Text style={[styles.navDesc, { color: colors.text.dim }, isActive && { color: colors.text.subtle }]}>
                    {item.description}
                  </Text>
                </View>
              </Hoverable>
            );
          })}
        </ScrollView>
      </View>

      <View style={[styles.right, { backgroundColor: colors.card.bg, borderColor: colors.card.border }]}>
        {renderContent()}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
    padding: REDESIGN_SPACING.horizontalPadding,
    gap: 16,
    backgroundColor: 'transparent',
  },
  left: {
    width: 380,
  },
  leftInner: {
    paddingBottom: 40,
    gap: 10,
  },
  profileCardWrapper: {
    marginBottom: 8,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 18,
    paddingHorizontal: 20,
    borderRadius: 16,
    borderWidth: 1,
  },
  navItemActive: {},
  navCopy: {
    flex: 1,
  },
  navTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
  },
  navTitleActive: {},
  navDesc: {
    ...REDESIGN_TYPOGRAPHY.caption,
    marginTop: 4,
  },
  navDescActive: {},
  right: {
    flex: 1,
    borderRadius: 20,
    overflow: 'hidden',
    borderWidth: 1,
  },
});

export default DesktopAccountScreen;
